import { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useData } from "@/context/data-provider";
import type { Doctor, Priority, Potential } from "@/types";
import { PRIO_LABELS, POT_LABELS } from "@/lib/constants";
import { calcScore, calcCompliance, isAtRisk, nextVisitDate } from "@/lib/scoring";
import { DoctorDetailModal } from "@/components/shared/doctor-detail-modal";
import { toCsv, downloadCsv } from "@/lib/csv";

type Filter = "all" | "alta" | "media" | "baixa" | "nok" | "ok" | "pot-alto";

const emptyDoctor = (): Omit<Doctor, "id"> => ({
  name: "", local: "", tel: "", email: "", prio: "media", pot: "medio",
  espec: "", notes: "", addr: "", prefDay: "", zone: "",
});

function scoreClass(s: number) {
  if (s >= 70) return "score-green";
  if (s >= 40) return "score-orange";
  return "score-red";
}

export default function Medicos() {
  const {
    doctors: { doctors, addDoctor, updateDoctor, deleteDoctor },
    visits: { visits },
    period: { period },
    zones: { zones },
    apoios: { apoios },
  } = useData();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Omit<Doctor, "id">>(emptyDoctor());
  const [detailDoc, setDetailDoc] = useState<Doctor | null>(null);

  const filtered = useMemo(() => {
    return doctors.filter((d) => {
      const q = search.toLowerCase();
      const matchSearch = !q || d.name.toLowerCase().includes(q) || d.local.toLowerCase().includes(q) || d.espec.toLowerCase().includes(q);
      if (!matchSearch) return false;
      switch (filter) {
        case "alta": case "media": case "baixa": return d.prio === filter;
        case "nok": return isAtRisk(d, visits, period);
        case "ok": return calcCompliance(d, visits, period).status === "ok";
        case "pot-alto": return d.pot === "alto";
        default: return true;
      }
    });
  }, [doctors, search, filter, visits, period]);

  const openAdd = () => { setEditingId(null); setForm(emptyDoctor()); setModalOpen(true); };
  const openEdit = (doc: Doctor) => { setEditingId(doc.id); const { id, ...rest } = doc; setForm(rest); setModalOpen(true); };
  const handleSave = () => {
    if (!form.name || !form.local) return;
    if (editingId) updateDoctor(editingId, form);
    else addDoctor(form);
    setModalOpen(false);
  };

  const exportDoctorsCsv = () => {
    const headers = ["Nome", "Local", "Especialidade", "Prioridade", "Potencial", "Telefone", "Email", "Morada", "Zona"];
    const rows = filtered.map((d) => [d.name, d.local, d.espec, PRIO_LABELS[d.prio], POT_LABELS[d.pot], d.tel, d.email, d.addr, d.zone]);
    downloadCsv("medicos.csv", toCsv(headers, rows));
  };

  return (
    <div className="container">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-serif text-[1.3rem]" style={{ color: "var(--mv-ink-dark)" }}>Médicos</h1>
        <div className="flex gap-2">
          <button className="mv-btn mv-btn-ghost mv-btn-sm" onClick={exportDoctorsCsv}>📥 CSV</button>
          <button className="mv-btn mv-btn-primary mv-btn-sm" onClick={openAdd}>+ Novo Médico</button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-[7px] mb-[13px] flex-wrap items-center">
        <input
          className="mv-input flex-1 min-w-[140px]"
          placeholder="Pesquisar nome, local, especialidade..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {([
          ["all", "Todos"], ["alta", "Alta Prioridade"], ["media", "Média"], ["baixa", "Baixa"],
          ["nok", "Em Risco"], ["ok", "Cumpridos"], ["pot-alto", "Alto Potencial"],
        ] as [Filter, string][]).map(([f, label]) => (
          <button key={f} className={`filter-btn ${filter === f ? "active" : ""}`} onClick={() => setFilter(f)}>
            {label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="mv-panel">
        <table className="mv-table">
          <thead>
            <tr>
              <th>Médico</th>
              <th className="hidden sm:table-cell">Local / Zona</th>
              <th>Prio.</th>
              <th className="hidden md:table-cell">Potencial</th>
              <th>Score</th>
              <th className="hidden lg:table-cell">Período</th>
              <th className="hidden md:table-cell">Estado</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <div className="empty-state"><div className="empty-icon">👨‍⚕️</div>Nenhum médico encontrado</div>
                </td>
              </tr>
            ) : (
              filtered.map((doc) => {
                const score = calcScore(doc, visits, period);
                const comp = calcCompliance(doc, visits, period);
                return (
                  <tr key={doc.id} onClick={() => setDetailDoc(doc)} className="cursor-pointer">
                    <td>
                      <div className="font-semibold">{doc.name}</div>
                      <div className="text-[0.72rem] sm:hidden" style={{ color: "var(--mv-ink-light)" }}>{doc.local}</div>
                    </td>
                    <td className="hidden sm:table-cell" style={{ color: "var(--mv-ink-light)", fontSize: "0.72rem" }}>{doc.local}</td>
                    <td><span className={`ptag ptag-${doc.prio}`}>{PRIO_LABELS[doc.prio]}</span></td>
                    <td className="hidden md:table-cell"><span className={`pottag pottag-${doc.pot}`}>{POT_LABELS[doc.pot]}</span></td>
                    <td><div className={`score-circle ${scoreClass(score)}`}>{score}</div></td>
                    <td className="hidden lg:table-cell">
                      <span className="font-mono text-[0.68rem]" style={{ color: "var(--mv-ink-light)" }}>{comp.actual}/{comp.target}</span>
                    </td>
                    <td className="hidden md:table-cell">
                      <span className={`ctag ctag-${comp.status === "ok" ? "ok" : comp.status === "partial" ? "partial" : "none"}`}>
                        {comp.status === "ok" ? "Cumprido" : comp.status === "partial" ? "Parcial" : "Sem visita"}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <button className="mv-btn mv-btn-ghost mv-btn-sm" onClick={(e) => { e.stopPropagation(); openEdit(doc); }}>✏</button>
                        <button className="mv-btn mv-btn-danger mv-btn-sm" onClick={(e) => { e.stopPropagation(); deleteDoctor(doc.id); }}>🗑</button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modalOpen && createPortal(
        <div
          className="fixed inset-0 z-[600] flex items-start justify-center"
          style={{ background: "rgba(15,46,90,0.5)", backdropFilter: "blur(3px)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}
        >
          <div
            className="mt-14 mx-4 w-full max-w-[600px] rounded-[16px] overflow-y-auto max-h-[80vh]"
            style={{
              background: "var(--mv-surface)",
              border: "1px solid var(--mv-border)",
              boxShadow: "0 20px 60px rgba(15,46,90,0.25)",
              animation: "modalIn 0.22s ease",
            }}
          >
            <div className="mv-panel-header sticky top-0 z-10 justify-between">
              <div className="mv-panel-title">{editingId ? "Editar Médico" : "Adicionar Médico"}</div>
              <button className="text-[1.15rem] opacity-40 hover:opacity-100 transition-opacity" onClick={() => setModalOpen(false)}>✕</button>
            </div>
            <div className="p-5 flex flex-col gap-3">
              <div className="section-title">Identificação</div>
              <div className="grid grid-cols-2 gap-[9px]">
                <div className="col-span-2">
                  <label className="mv-label">Nome completo *</label>
                  <input className="mv-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Dr. João Silva" />
                </div>
                <div className="col-span-2">
                  <label className="mv-label">Local de Trabalho *</label>
                  <input className="mv-input" value={form.local} onChange={(e) => setForm({ ...form, local: e.target.value })} placeholder="Hospital..." />
                </div>
                <div>
                  <label className="mv-label">Especialidade</label>
                  <input className="mv-input" value={form.espec} onChange={(e) => setForm({ ...form, espec: e.target.value })} />
                </div>
                <div>
                  <label className="mv-label">Zona Geográfica</label>
                  <select className="mv-select" value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })}>
                    <option value="">— Selecionar —</option>
                    {zones.map((z) => <option key={z} value={z}>{z}</option>)}
                  </select>
                </div>
              </div>
              <div className="section-title">Classificação</div>
              <div className="grid grid-cols-2 gap-[9px]">
                <div>
                  <label className="mv-label">Prioridade</label>
                  <select className="mv-select" value={form.prio} onChange={(e) => setForm({ ...form, prio: e.target.value as Priority })}>
                    <option value="alta">Alta</option>
                    <option value="media">Média</option>
                    <option value="baixa">Baixa</option>
                  </select>
                </div>
                <div>
                  <label className="mv-label">Potencial</label>
                  <select className="mv-select" value={form.pot} onChange={(e) => setForm({ ...form, pot: e.target.value as Potential })}>
                    <option value="alto">Alto</option>
                    <option value="medio">Médio</option>
                    <option value="baixo">Baixo</option>
                  </select>
                </div>
                <div>
                  <label className="mv-label">Dia Preferido</label>
                  <select className="mv-select" value={form.prefDay} onChange={(e) => setForm({ ...form, prefDay: e.target.value })}>
                    <option value="">—</option>
                    <option value="1">Segunda</option>
                    <option value="2">Terça</option>
                    <option value="3">Quarta</option>
                    <option value="4">Quinta</option>
                    <option value="5">Sexta</option>
                  </select>
                </div>
              </div>
              <div className="section-title">Contactos</div>
              <div className="grid grid-cols-2 gap-[9px]">
                <div><label className="mv-label">Telefone</label><input className="mv-input" value={form.tel} onChange={(e) => setForm({ ...form, tel: e.target.value })} /></div>
                <div><label className="mv-label">Email</label><input className="mv-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div className="col-span-2"><label className="mv-label">Morada</label><input className="mv-input" value={form.addr} onChange={(e) => setForm({ ...form, addr: e.target.value })} /></div>
              </div>
              <div className="section-title">Notas</div>
              <textarea className="mv-textarea" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Observações..." />
            </div>
            <div className="p-[12px_20px] flex gap-2 justify-end" style={{ borderTop: "1px solid var(--mv-border)", background: "#FAFCFF" }}>
              <button className="mv-btn mv-btn-ghost" onClick={() => setModalOpen(false)}>Cancelar</button>
              <button className="mv-btn mv-btn-primary" onClick={handleSave} disabled={!form.name || !form.local}>
                {editingId ? "Guardar" : "Adicionar"}
              </button>
            </div>
          </div>

        </div>,
        document.body
      )}

      {detailDoc && (
        <DoctorDetailModal
          doctor={detailDoc}
          visits={visits}
          period={period}
          apoios={apoios}
          onClose={() => setDetailDoc(null)}
          onEdit={() => { openEdit(detailDoc); setDetailDoc(null); }}
          onDelete={() => { deleteDoctor(detailDoc.id); setDetailDoc(null); }}
        />
      )}
    </div>
  );
}
